import { Resource } from "../../types/resource";

export const parseArtifact = (
  content: string | null | undefined,
  id: string,
  count: number,
  isLoading: boolean = false,
  messageId?: string
): Resource => {
  try {
    // Handle null or undefined content
    if (!content || typeof content !== 'string') {
      return {
        id,
        type: 'essay',
        title: `Invalid Resource ${count}`,
        content: 'Invalid content received',
        timestamp: Date.now().toString(),
        chatId: '',
      };
    }

    // Preserve visible Unicode characters from model output and only strip
    // control characters that can interfere with parsing/rendering.
    // eslint-disable-next-line no-control-regex -- intentionally matching control chars to remove them
    const sanitizedContent = content.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '');

    if (sanitizedContent.includes('<essay>')) {
      const titleMatch = sanitizedContent.match(/<title>([\s\S]*?)(?=<\/title>|$)/);
      // Try non-greedy match for complete <content> tag
      let contentMatch = sanitizedContent.match(/<content>([\s\S]*?)<\/content>/);
      // Fallback for unclosed <content> tag: capture until end of string
      if (!contentMatch) {
        contentMatch = sanitizedContent.match(/<content>([\s\S]*)/);
      }

      return {
        id,
        type: 'essay',
        title: titleMatch ? titleMatch[1].trim() : isLoading ? 'Generating Essay...' : `Essay ${count}`,
        content: contentMatch ? contentMatch[1].trim() : '',
        messageId: messageId ?? undefined,
        timestamp: Date.now().toString(),
        chatId: '',
      };
    } else if (sanitizedContent.includes('<code')) {
      const languageMatch = sanitizedContent.match(/language="(.*?)"/);
      const titleMatch = sanitizedContent.match(/title="([\s\S]*?)(?="|$)/);
      // Try non-greedy match for complete <code> tag
      let codeMatch = sanitizedContent.match(/<code.*?>([\s\S]*?)<\/code>/);
      // Fallback for unclosed <code> tag: capture until end
      if (!codeMatch) {
        codeMatch = sanitizedContent.match(/<code.*?>([\s\S]*)/);
      }

      // Strip any legacy <isExecutable> tag if it's still emitted by older content.
      let codeContent = codeMatch ? codeMatch[1] : sanitizedContent;
      if (typeof codeContent === 'string') {
        codeContent = codeContent.replace(/<isExecutable>(true|false)<\/isExecutable>/gi, '').trim();
      }

      return {
        id,
        type: 'code',
        title: titleMatch ? titleMatch[1].trim() : isLoading ? 'Generating Code...' : `Code Snippet ${count}`,
        language: languageMatch ? languageMatch[1].trim() : 'plaintext',
        content: codeContent,
        messageId: messageId ?? undefined,
        timestamp: Date.now().toString(),
        chatId: '',
      };
    } else if (sanitizedContent.includes('<visualization')) {
      // Try non-greedy match for complete <visualization> tag
      let vizMatch = sanitizedContent.match(/<visualization.*?>([\s\S]*?)<\/visualization>/);
      // Fallback for unclosed <visualization> tag: capture until end
      if (!vizMatch) {
        vizMatch = sanitizedContent.match(/<visualization.*?>([\s\S]*)/);
      }
      const titleMatch = sanitizedContent.match(/title="([\s\S]*?)(?="|$)/);

      if (vizMatch && titleMatch) {
        const vizContent = vizMatch[1];
        try {
          JSON.parse(vizContent);
          return {
            id,
            type: 'visualization',
            title: titleMatch ? titleMatch[1].trim() : isLoading ? 'Generating Visualization...' : `Visualization ${count}`,
            content: vizContent,
            messageId: messageId ?? undefined,
            timestamp: Date.now().toString(),
            chatId: '',
          };
        } catch (error) {
          // Invalid JSON but still a visualization
          return {
            id,
            type: 'visualization',
            title: titleMatch[1].trim(),
            content: vizContent,
            messageId: messageId ?? undefined,
            timestamp: Date.now().toString(),
            chatId: '',
          };
        }
      } else if (titleMatch) {
        // Missing or malformed visualization content
        return {
          id,
          type: 'visualization',
          title: titleMatch ? titleMatch[1].trim() : isLoading ? 'Generating Visualization...' : `Visualization ${count}`,
          content: '',
          messageId: messageId ?? undefined,
          timestamp: Date.now().toString(),
          chatId: '',
        };
      }
    }

    // Default case for unrecognized content
    return {
      id,
      type: 'essay',
      title: isLoading ? 'Generating Resource...' : `Resource ${count}`,
      content: sanitizedContent,
      messageId: messageId ?? undefined,
      timestamp: Date.now().toString(),
      chatId: '',
    };
  } catch (error) {
    return {
      id,
      type: 'essay',
      title: isLoading ? 'Generating Resource...' : `Error in Resource ${count}`,
      content: error instanceof Error ? error.message : 'Unknown error',
      messageId: messageId ?? undefined,
      timestamp: Date.now().toString(),
      chatId: '',
    };
  }
};  
